import { useState } from 'react';
import { ArrowLeft, Save } from 'lucide-react';
import { guardUnsavedBack } from '../../../lib/formGuard';
import type { NOKDetails } from '../../../types/prf';

interface NOKDetailsSectionProps {
  data: NOKDetails;
  onSave: (data: NOKDetails) => void;
  onBack: () => void;
}

export default function NOKDetailsSection({
  data,
  onSave,
  onBack,
}: NOKDetailsSectionProps) {
  const [formData, setFormData] = useState<NOKDetails>(data);

  const handleChange = (field: keyof NOKDetails, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSave = () => {
    onSave(formData);
  };

  const handleBackClick = () => guardUnsavedBack(data, formData, onBack);

  const relationshipOptions = [
    'Spouse',
    'Partner',
    'Parent',
    'Child',
    'Sibling',
    'Grandparent',
    'Grandchild',
    'Friend',
    'Carer',
    'Other',
  ];

  return (
    <div className="flex flex-col h-full bg-background">
      {/* Header */}
      <div className="flex-shrink-0 border-b border-border bg-card">
        <div className="flex items-center justify-between px-4 py-3">
          <div className="flex items-center gap-3">
            <button
              onClick={handleBackClick}
              className="p-1.5 rounded-lg hover:bg-accent transition-colors"
            >
              <ArrowLeft className="w-5 h-5 text-muted-foreground" />
            </button>
            <div>
              <h2 className="text-lg font-bold text-foreground">NOK Details</h2>
              <p className="text-xs text-muted-foreground">Next of Kin information</p>
            </div>
          </div>
        </div>
      </div>

      {/* Form Content */}
      <div className="flex-1 overflow-y-auto p-4">
        <div className="max-w-2xl mx-auto space-y-4">
          {/* Next of Kin Name */}
          <div>
            <label className="block text-sm font-semibold text-foreground mb-2">
              Next of Kin Name
            </label>
            <input
              type="text"
              value={formData.name || ''}
              onChange={(e) => handleChange('name', e.target.value)}
              className="w-full px-3 py-2.5 rounded-lg bg-background border-2 border-border text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary transition-colors"
              placeholder="Enter next of kin full name"
            />
          </div>

          {/* Next of Kin Contact Details */}
          <div>
            <label className="block text-sm font-semibold text-foreground mb-2">
              Next of Kin Contact Details
            </label>
            <input
              type="tel"
              value={formData.phoneNumber || ''}
              onChange={(e) => handleChange('phoneNumber', e.target.value)}
              className="w-full px-3 py-2.5 rounded-lg bg-background border-2 border-border text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary transition-colors"
              placeholder="Enter phone number or email"
            />
          </div>

          {/* Relationship */}
          <div>
            <label className="block text-sm font-semibold text-foreground mb-2">
              Relationship
            </label>
            <select
              value={formData.relationship || ''}
              onChange={(e) => handleChange('relationship', e.target.value)}
              className="w-full px-3 py-2.5 rounded-lg bg-background border-2 border-border text-foreground focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary transition-colors appearance-none cursor-pointer"
            >
              <option value="">Select relationship</option>
              {relationshipOptions.map((option) => (
                <option key={option} value={option}>
                  {option}
                </option>
              ))}
            </select>
          </div>

          {/* Address */}
          <div>
            <label className="block text-sm font-semibold text-foreground mb-2">
              Address
            </label>
            <input
              type="text"
              value={formData.address || ''}
              onChange={(e) => handleChange('address', e.target.value)}
              className="w-full px-3 py-2.5 rounded-lg bg-background border-2 border-border text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary transition-colors"
              placeholder="Enter address"
            />
          </div>
        </div>
      </div>

      {/* Footer with Save Button */}
      <div className="flex-shrink-0 border-t border-border bg-card p-4">
        <button
          onClick={handleSave}
          className="w-full py-3 rounded-lg bg-green-600 hover:bg-green-700 text-white font-semibold text-sm transition-all flex items-center justify-center gap-2"
        >
          <Save className="w-4 h-4" />
          Save
        </button>
      </div>
    </div>
  );
}
